import { useMemo } from "react";
import type { ProtoLeagueTable } from "@catbento/shared";
import { readSnapshot } from "../data/snapshot.js";
import {
  getLeagueCompetitions,
  getLeagueTable,
  type CompetitionId,
  type LeagueChoice,
} from "../data/league.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { loadLeagueChoices, loadLeagueTable, type LiveLeagueChoice } from "../live/league.js";
import { KNOWN_LEAGUE_NAMES } from "../live/known-leagues.js";

// Phase 02 seam: the league group's reads, by league id (ids-2). Track 2 (T2-1): with
// ?live=1 the same two hooks read app-api (/competitions + /seasons + /league-table) and
// adapt to the same shapes (live/league.ts); without it, the snapshot, exactly as before.
// The source is picked once, when this file loads (LIVE is fixed per page).

function useSnapshotLeagueCompetitions(): LeagueChoice[] {
  return useMemo(() => getLeagueCompetitions(readSnapshot()), []);
}

function useSnapshotLeagueTable(competitionId: CompetitionId | null, season?: string): ProtoLeagueTable | null {
  return useMemo(
    () => (competitionId === null ? null : getLeagueTable(readSnapshot(), competitionId, season)),
    [competitionId, season],
  );
}

/** The live league list. Match Results (T2-2) uses it too, under the same key: the two
 *  widgets switch into each other on the same module, so a switch reuses the list that
 *  module already has (its own answer — not sharing between widgets). */
export function useLiveLeagueChoices(): LiveLeagueChoice[] {
  // Non-null: the key is never null here.
  return useLiveQuery("league:choices", () => loadLeagueChoices(liveApi(), KNOWN_LEAGUE_NAMES)) as LiveLeagueChoice[];
}

function useLiveLeagueTable(competitionId: CompetitionId | null, season?: string): ProtoLeagueTable | null {
  const league = useLiveLeagueChoices().find((c) => c.id === competitionId) ?? null;
  // An unknown id → no table (the widget's own empty state), as the snapshot hook does.
  const key = league ? `league:table:${league.id}:${season ?? "newest"}` : null;
  return useLiveQuery(key, () => loadLeagueTable(liveApi(), league as LeagueChoice, season));
}

/** The leagues that have a league table — the snapshot's (Squad's picker on the snapshot site;
 *  its live path uses `useLiveLeagueChoices`). */
export const useLeagueCompetitions: () => LeagueChoice[] = useSnapshotLeagueCompetitions;

/** The League Table's own league list: live with ?live=1. A separate hook so going live is
 *  per WIDGET (track2-plan.md §1) — Squad keeps the snapshot list until its own slice. */
export const useLeagueTableCompetitions: () => LeagueChoice[] = LIVE ? useLiveLeagueChoices : useSnapshotLeagueCompetitions;

/** A league's standings for a season (default = its newest). Null if uncaptured. */
export const useLeagueTable: (competitionId: CompetitionId | null, season?: string) => ProtoLeagueTable | null = LIVE
  ? useLiveLeagueTable
  : useSnapshotLeagueTable;
